import { ConnectionStatus } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { local } from "~/core/local";
import { FEATURES } from "./features";
import { MakeStatusParams } from "./interfaces";

const START_TS = Date.now();

const access = (obj: Record<string, unknown>, parts: string[]) => {
    let current = obj;

    for (const part of parts) {
        if (!current[part]) {
            current[part] = {};
        }

        current = current[part] as Record<string, unknown>;
    }

    return current;
};

const parse = ({ features }: ReturnType<typeof featuresSeenLocal.get>) => {
    const result: Record<string, unknown> = {};

    for (const feature of features) {
        const parts = feature.split("_");
        const name = parts.pop() as string;
        const current = access(result, parts);

        current[name] = true;
    }

    return result;
};

const feed = (obj: Record<string, unknown>) => {
    for (const feature of FEATURES) {
        const parts = feature.split("_");
        const name = parts.pop() as string;
        const current = access(obj, parts);

        if (!(name in current)) {
            current[name] = false;
        }
    }

    return obj;
};

const isSeen = (
    item: Record<string, unknown> | boolean | undefined | null | unknown,
): boolean => {
    if (item === undefined || item === null) {
        // if it's not explicitly set to false, consider it seen
        return true;
    }

    if (typeof item === "boolean") {
        return item;
    }

    return Object.values(item).every(isSeen);
};

const featuresSeenLocal = local("v1_features_seen", {
    features: [] as string[],
});

const makeStatus = ({ api }: MakeStatusParams) => {
    const loading = signal(false);
    const featuresTag = signal(0);
    const featuresSeen: Record<string, ReadonlySignal<boolean>> = {};
    const featuresData = feed(parse(featuresSeenLocal.get()));
    const online = signal(navigator.onLine);
    const apiLost = signal(false);
    const lost = computed(() => apiLost.value || !online.value);
    const connection = signal(ConnectionStatus.Connecting);
    const startTs = START_TS;
    let apiLostTm = 0;

    const isUnseen = (key: string) => {
        if (!(key in featuresSeen)) {
            featuresSeen[key] = computed(() => {
                void featuresTag.value;

                const parts = key.split("_");
                const name = parts.pop() as string;
                const obj = access(featuresData, parts);

                return !isSeen(obj[name]);
            });
        }

        return featuresSeen[key];
    };

    const seen = (...keys: string[]) => {
        let changed = false;

        for (const key of keys) {
            const parts = key.split("_");
            const name = parts.pop() as string;
            const obj = access(featuresData, parts);

            if (!obj[name]) {
                obj[name] = true;
                changed = true;
            }
        }

        if (changed) {
            featuresSeenLocal.set({
                features: featuresSeenLocal.get().features.concat(keys),
            });
            featuresTag.value = featuresTag.peek() + 1;
        }
    };

    const restore = () => {
        if (apiLost.peek()) {
            apiLost.value = false;
        }
    };

    const cleanup = () => {
        if (apiLostTm) {
            window.clearTimeout(apiLostTm);
            apiLostTm = 0;
        }
    };

    const wait = (ms: number) => {
        if (apiLostTm) {
            return;
        }

        apiLostTm = window.setTimeout(() => {
            if (
                connection.peek() !== ConnectionStatus.Authenticated &&
                connection.peek() !== ConnectionStatus.Connected
            ) {
                apiLost.value = true;
            }
        }, ms);
    };

    const unsubscribe = api.connection.subscribe((con) => {
        if (con) {
            connection.value = con.status.value;
            con.status.listen((value) => {
                connection.value = value;

                // @DEP @LINK routing dependency
                if (window.location.pathname === "/p/login") {
                    return true;
                }

                if (value === ConnectionStatus.Authenticated) {
                    cleanup();
                    restore();
                } else {
                    wait(Date.now() - startTs > 600000 ? 5_000 : 15_000);
                }

                return true;
            });

            return unsubscribe;
        }
    });

    window.addEventListener("online", () => {
        online.value = true;
    });

    window.addEventListener("offline", () => {
        online.value = false;
    });

    return {
        isUnseen,
        seen,
        lost,
        online,
        loading,
        apiLost,
    };
};

export default makeStatus;
